import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  styled,
  useTheme,
  TextField,
  Stack,
  InputAdornment,
  Tooltip,
  Typography,
} from "@mui/material";
// import { Tabs, Tab } from "@mui/material";
import Refresh from "@mui/icons-material/Refresh";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate, useLocation } from "react-router-dom";
import { getAppointmentsGroupedByPatient } from "../../services/appointmentService";
import EditPatientModal from "../AddPatientModal/editPatientModal.tsx";
import Calendar from "../calendar/calendar.jsx";
import { useAuth } from "../../context/AuthContext.jsx";
import { getPatientById, getAllPatients } from "../../services/patientService";
import { getDoctorByUserId, getAllDoctors } from "../../services/doctorService";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import NewConsultationModal from "../AddPatientModal/OutPatientAppointmentModal.tsx";
import FrontDeskVitalSignsPanel from "../../components/FrontDeskVitalSignsPanel";
import { getAllDepartment } from "../../services/departmentService";
import {
  buildDepartmentsLookup,
  buildDoctorsLookup,
  buildOpdPeriodSummary,
  buildOpdPeriodSummaryForPeriod,
  buildAlignedOpdPeriodExport,
  buildMonthlyOpdExcelTableRows,
  buildOpdVisitInPeriodPatientSet,
  buildRegistrationPatientLookup,
  formatPeriodLabel,
  getAppointmentDate,
  getPatientFromDetailsMap,
  getPatientRegistrationRaw,
  getRegistrationDateKey,
  isSingleDayPeriod,
  isVisitDateInPeriod,
  parsePeriodBounds,
  resolveAssignedDoctorNameFromAppointments,
  toPeriodDateKey,
} from "../../utils/patientPeriodSummary";
import { downloadPatientTableExcel } from "../../utils/patientTableExcelExport";
import { getOrganizationName } from "../../utils/organizationUtils";
import { OPD_PATIENTS_REFRESH_EVENT } from "../../utils/opdPatientsRefresh";
import { fetchPeriodInvoicesForSummary, normalizeApiArray } from "../../utils/invoiceTableUtils";
import {
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
} from "../../utils/pageLayoutStyles";
import {
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
} from "../../utils/formModalStyles";

const columns = [
  { id: "patientId", label: "Patient ID" },
  { id: "patientName", label: "Patient Name" },
  { id: "email", label: "Email" },
  { id: "mobile", label: "Mobile" },
  { id: "gender", label: "Gender" },
  { id: "age", label: "Age" },
  { id: "registrationDate", label: "Registration Date" },
  { id: "", label: "Action" },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: "bold",
  },
}));

// Module-level (not component-instance) so this survives PatientsTable being
// unmounted/remounted by MUI's TabPanel on every "Out Patients" tab switch —
// a useRef alone gets thrown away with the component, defeating the 5-minute
// TTL below on every revisit.
let __patientsAppointmentsCache = { data: null, timestamp: null };

function PatientsTable({ doctorId, onPatientClick }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fetching, setFetching] = useState(true);
  const [patientsList, setPatientsList] = useState([]);
  const [patientInfo, setPatientInfo] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openCalendarModal, setOpenCalendarModal] = useState(false);
  const [openNewConsultationModal, setOpenNewConsultationModal] = useState(false);
  const [newConsultationPatientInfo, setNewConsultationPatientInfo] = useState(null);
  const [openVitalsModal, setOpenVitalsModal] = useState(false);
  const [vitalsPatient, setVitalsPatient] = useState(null);
  const [vitalsMode, setVitalsMode] = useState("form");
  const [vitalsSaving, setVitalsSaving] = useState(false);
  const [vitalsCanSave, setVitalsCanSave] = useState(false);
  const vitalsRef = useRef(null);
  // const [subTab, setSubTab] = useState("upcoming");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterGender, setFilterGender] = useState("");
  const [filterAgeMin, setFilterAgeMin] = useState("");
  const [filterAgeMax, setFilterAgeMax] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [patientDetailsMap, setPatientDetailsMap] = useState({});
  const [currentDoctorId, setCurrentDoctorId] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState({ loaded: 0, total: 0, message: "" });
  const [appointmentsCache, setAppointmentsCache] = useState(null);
  const [cacheTimestamp, setCacheTimestamp] = useState(null);
  const [periodInvoices, setPeriodInvoices] = useState([]);
  const [doctorsById, setDoctorsById] = useState({});
  const [departmentsById, setDepartmentsById] = useState({});
  const fetchInProgressRef = useRef(false);
  const pendingRefreshRef = useRef(false);
  const fetchPatientsListRef = useRef(null);
  const refreshPatientsListRef = useRef(null);
  // Guards state updates from a fetch that's still in flight when this
  // component unmounts (e.g. navigating away from the Patients tab quickly).
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const theme = useTheme();

  // Fetch doctor ID if user is a doctor
  useEffect(() => {
    const fetchCurrentDoctorId = async () => {
      if (user && (user.roleName === "doctor" || user.roleName === "surgeon")) {
        try {
          const doctorDetails = await getDoctorByUserId(user.id);
          if (isMountedRef.current) setCurrentDoctorId(doctorDetails.id);
        } catch (error) {
          console.error("Failed to fetch doctor ID:", error);
        }
      }
    };
    fetchCurrentDoctorId();
  }, [user]);

  useEffect(() => {
    fetchPatientsList();
  }, [location.key]);

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const departments = await getAllDepartment();
        setDepartmentsById(buildDepartmentsLookup(departments));
      } catch (error) {
        console.error("Failed to fetch departments:", error);
      }
    };
    loadDepartments();
  }, []);

  const buildPatientDetailsMap = (patients) => {
    const map = {};
    const list = Array.isArray(patients) ? patients : normalizeApiArray(patients);
    const addPatientKeys = (patient, id) => {
      if (id == null || id === "") return;
      map[id] = patient;
      map[String(id)] = patient;
      const numericId = Number(id);
      if (!Number.isNaN(numericId)) {
        map[numericId] = patient;
      }
    };

    list.forEach((patient) => {
      addPatientKeys(patient, patient?.id);
      addPatientKeys(patient, patient?.patientId);
      addPatientKeys(patient, patient?.patient_id);
    });
    return map;
  };

  const buildPatientNameIndex = (detailsMap) => {
    const index = new Map();
    const seen = new Set();
    Object.values(detailsMap || {}).forEach((patient) => {
      const id = patient?.id ?? patient?.patientId ?? patient?.patient_id;
      if (id == null || id === "" || seen.has(String(id))) return;
      seen.add(String(id));
      const name = String(patient?.name || "").trim().toLowerCase();
      if (!name || name === "n/a" || name === "null") return;
      if (!index.has(name)) index.set(name, []);
      index.get(name).push(patient);
    });
    return index;
  };

  /** Pull display fields from camelCase or snake_case patient payloads. */
  const getPatientExportFields = (patient) => {
    if (!patient || typeof patient !== "object") {
      return {
        email: "",
        mobile: "",
        gender: "",
        age: "",
        creationDate: "",
      };
    }
    const email =
      patient.email ||
      patient.emailId ||
      patient.email_id ||
      "";
    const mobile =
      patient.mobile ||
      patient.phone ||
      patient.contactNumber ||
      patient.contact_number ||
      patient.mobileNumber ||
      patient.mobile_number ||
      "";
    const gender = patient.gender || patient.sex || "";
    const dobRaw =
      patient.dob ||
      patient.dateOfBirth ||
      patient.date_of_birth ||
      patient.birthDate ||
      patient.birth_date ||
      null;
    const age = dobRaw
      ? Math.max(
          0,
          Math.floor(
            (Date.now() - new Date(dobRaw).getTime()) /
              (365.25 * 24 * 60 * 60 * 1000)
          )
        )
      : "";
    const creationDate = getRegistrationDateKey(patient) || "";
    return {
      email: email ? String(email) : "",
      mobile: mobile ? String(mobile) : "",
      gender: gender ? String(gender) : "",
      age: age === "" ? "" : age,
      creationDate,
    };
  };

  /** Fill gaps when getAllPatients is incomplete — fetch missing masters by id. */
  const hydratePatientDetailsForExport = async (rows, baseMap) => {
    const map = { ...baseMap };
    const addToMap = (id, patient) => {
      if (!patient || id == null || id === "") return;
      map[id] = patient;
      map[String(id)] = patient;
      const numericId = Number(id);
      if (!Number.isNaN(numericId)) map[numericId] = patient;
      const pid = patient.id ?? patient.patientId ?? patient.patient_id;
      if (pid != null && pid !== "" && String(pid) !== String(id)) {
        map[pid] = patient;
        map[String(pid)] = patient;
      }
    };

    const missingIds = [];
    const seen = new Set();
    (rows || []).forEach((row) => {
      const id = row?.patientId ?? row?.patient_id;
      if (id == null || id === "") return;
      if (getPatientFromDetailsMap(map, id)) return;
      const key = String(id);
      if (seen.has(key)) return;
      seen.add(key);
      missingIds.push(id);
    });

    for (let i = 0; i < missingIds.length; i += 20) {
      const batch = missingIds.slice(i, i + 20);
      const results = await Promise.allSettled(
        batch.map((id) => getPatientById(id))
      );
      results.forEach((result, index) => {
        if (result.status !== "fulfilled" || !result.value) return;
        addToMap(batch[index], result.value);
      });
    }
    return map;
  };

  const getAssignedDoctorName = (appointment, doctorsLookup) => {
    if (appointment?.doctorName || appointment?.doctor_name) {
      return appointment.doctorName || appointment.doctor_name;
    }
    const doctorId = appointment?.doctorId ?? appointment?.doctor_id;
    if (doctorId == null || doctorId === "") return null;
    const doctor =
      doctorsLookup[doctorId] ||
      doctorsLookup[String(doctorId)] ||
      doctorsLookup[Number(doctorId)];
    return doctor?.name || `Dr. ID: ${doctorId}`;
  };

  const invalidateAppointmentsCache = () => {
    __patientsAppointmentsCache = { data: null, timestamp: null };
    setAppointmentsCache(null);
    setCacheTimestamp(null);
  };

  const fetchPatientsList = async (options = {}) => {
    const { forceRefresh = false, silent = false } = options;

    if (fetchInProgressRef.current) {
      // Do not drop refreshes while an earlier load is still in flight (e.g. while
      // auto-cancel reminder calls briefly saturate the network).
      pendingRefreshRef.current = true;
      return;
    }
    fetchInProgressRef.current = true;

    if (!silent) {
      setFetching(true);
      setLoadingProgress({ loaded: 0, total: 0, message: "Loading..." });
    }
    
    try {
      // Check cache (5 minute TTL) — skipped when forceRefresh is true (auto/manual refresh)
      const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
      const now = Date.now();
      const cacheSnapshot = __patientsAppointmentsCache;
      const cachedData =
        !forceRefresh &&
        cacheSnapshot.data &&
        cacheSnapshot.timestamp &&
        now - cacheSnapshot.timestamp < CACHE_TTL
          ? cacheSnapshot.data
          : null;

      let data;
      if (cachedData) {
        console.log("Using cached appointments data");
        data = cachedData;
        // The module-level cache can carry data from before this component
        // instance existed (e.g. remounted after a tab switch) — always sync
        // it into local state so memos/renders relying on appointmentsCache
        // see it too, not just when this call is the one that fetched it.
        setAppointmentsCache(cachedData);
        setCacheTimestamp(cacheSnapshot.timestamp);
        if (!silent) {
          setLoadingProgress({ loaded: 0, total: 0, message: "Loading patient details..." });
        }
      } else if (!silent) {
        console.log("Starting to fetch appointments from API...");
        setLoadingProgress({ loaded: 0, total: 0, message: "Loading..." });
      }

      const startTime = Date.now();
      const appointmentsPromise = cachedData
        ? Promise.resolve(cachedData)
        : getAppointmentsGroupedByPatient().catch((err) => {
            console.error("[PatientsTable] Error fetching appointments:", err);
            return {};
          });

      const [appointmentsResult, doctorsResult, allPatientsResult] = await Promise.all([
        appointmentsPromise,
        getAllDoctors({ forceRefresh: false }).catch(() => []),
        getAllPatients().catch((err) => {
          console.error("[PatientsTable] Error fetching patients:", err);
          return [];
        }),
      ]);

      // The component may have unmounted (e.g. navigated away) while the
      // above network calls were in flight — skip applying results in that case.
      if (!isMountedRef.current) return;

      data = appointmentsResult;
      const doctorsLookup = buildDoctorsLookup(doctorsResult);
      setDoctorsById(doctorsLookup);
      setPatientDetailsMap(buildPatientDetailsMap(allPatientsResult));

      if (!cachedData) {
        const fetchTime = ((Date.now() - startTime) / 1000).toFixed(1);
        console.log(
          `[PatientsTable] Fetched appointments in ${fetchTime}s (${data ? Object.keys(data).length : 0} patient groups)`
        );

        if (!data || typeof data !== "object") {
          console.warn(`[PatientsTable] Invalid data format, setting to empty object`);
          data = {};
        }

        if (Object.keys(data).length === 0) {
          console.warn(`[PatientsTable] No patient groups found in response`);
        }

        __patientsAppointmentsCache = { data, timestamp: now };
        setAppointmentsCache(data);
        setCacheTimestamp(now);
      }
      
      // Step 2: Format appointment rows + include OUT patients without appointments
      setLoadingProgress({ loaded: 0, total: 0, message: "Processing appointments..." });
      
      const formattedList = Object.values(data)
        .map((arr) => {
          if (!Array.isArray(arr) || arr.length === 0) return null;
          return arr.reduce((latest, apt) => {
            if (!apt) return latest;
            if (!latest) return apt;
            const latestDate = getAppointmentDate(latest);
            const aptDate = getAppointmentDate(apt);
            if (!latestDate) return apt;
            if (!aptDate) return latest;
            return aptDate.getTime() > latestDate.getTime() ? apt : latest;
          }, null);
        })
        .filter(Boolean)
        .filter((apt) => {
          // Ensure appointment has at least patientId or patientName to be valid
          if (!apt.patientId && !apt.patientName) {
            console.warn(`[PatientsTable] Filtering out appointment ${apt.id} - missing patientId and patientName`);
            return false;
          }
          return true;
        });

      const appointmentPatientIds = new Set(
        formattedList
          .map((apt) => (apt.patientId != null ? String(apt.patientId) : null))
          .filter(Boolean)
      );

      // OPD patients created without appointment must still appear in Out Patients
      const outPatientsWithoutAppointment = (Array.isArray(allPatientsResult) ? allPatientsResult : [])
        .filter((patient) => {
          const id = patient?.id ?? patient?.patientId ?? patient?.patient_id;
          if (id == null || id === "") return false;
          if (appointmentPatientIds.has(String(id))) return false;

          const type = String(patient.patientType || patient.patient_type || "OUT")
            .toUpperCase()
            .trim();
          return type === "OUT" || type === "";
        })
        .map((patient) => {
          const id = patient.id ?? patient.patientId ?? patient.patient_id;
          return {
            id: `patient-only-${id}`,
            patientId: id,
            patientName: patient.name || patient.patientName || "",
            doctorId: null,
            doctorName: null,
            appointmentDateTime: null,
            isPatientOnlyRow: true,
            createdAt: patient.createdAt || patient.created_at || null,
          };
        });

      const mergedList = [...formattedList, ...outPatientsWithoutAppointment].sort((a, b) => {
        const idA = Number(a.patientId) || 0;
        const idB = Number(b.patientId) || 0;
        return idB - idA; // Patient ID descending
      });
      
      console.log(
        `[PatientsTable] Processed ${formattedList.length} appointment rows + ${outPatientsWithoutAppointment.length} OUT patients without appointment`
      );

      setPatientsList(mergedList);
      if (!silent) {
        setLoadingProgress({ loaded: 0, total: 0, message: "" });
      }
    } catch (err) {
      console.error("Failed to fetch patients list:", err);
      if (isMountedRef.current) {
        if (!silent) {
          setLoadingProgress({ loaded: 0, total: 0, message: "Error loading data" });
        }
        setPatientsList([]);
      }
    } finally {
      fetchInProgressRef.current = false;
      if (!silent) {
        setFetching(false);
      }
      if (pendingRefreshRef.current) {
        pendingRefreshRef.current = false;
        // Re-run once the blocking request finished so the table does not stay empty
        // until a full browser refresh.
        Promise.resolve().then(() => {
          refreshPatientsListRef.current?.({ silent: true, forceRefresh: true });
        });
      }
    }
  };

  fetchPatientsListRef.current = fetchPatientsList;

  const refreshPatientsList = (options = {}) => {
    invalidateAppointmentsCache();
    return fetchPatientsList({ forceRefresh: true, ...options });
  };

  refreshPatientsListRef.current = refreshPatientsList;

  useEffect(() => {
    const handleOpdPatientsRefresh = () => {
      if (fetchInProgressRef.current) {
        pendingRefreshRef.current = true;
        return;
      }
      refreshPatientsListRef.current?.({ silent: true });
    };

    window.addEventListener(OPD_PATIENTS_REFRESH_EVENT, handleOpdPatientsRefresh);

    return () => {
      window.removeEventListener(OPD_PATIENTS_REFRESH_EVENT, handleOpdPatientsRefresh);
    };
  }, []);

  const visitPeriodBounds = parsePeriodBounds(fromDate, toDate);

  useEffect(() => {
    if (!fromDate && !toDate) {
      setPeriodInvoices([]);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await fetchPeriodInvoicesForSummary(fromDate, toDate);
        if (!cancelled) {
          setPeriodInvoices(data);
        }
      } catch (error) {
        console.warn("[PatientsTable] Could not load invoices for period summary:", error);
        if (!cancelled) {
          setPeriodInvoices([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fromDate, toDate]);

  const registrationLookup = useMemo(
    () =>
      fromDate || toDate
        ? buildRegistrationPatientLookup(
            periodInvoices,
            visitPeriodBounds.start,
            visitPeriodBounds.end,
            fromDate || null,
            toDate || null,
            patientDetailsMap
          )
        : null,
    [periodInvoices, fromDate, toDate, visitPeriodBounds.start, visitPeriodBounds.end, patientDetailsMap]
  );

  const visitInPeriodPatientIds = useMemo(
    () =>
      buildOpdVisitInPeriodPatientSet(
        appointmentsCache || {},
        visitPeriodBounds.start,
        visitPeriodBounds.end,
        fromDate || null,
        toDate || null
      ),
    [appointmentsCache, visitPeriodBounds.start, visitPeriodBounds.end, fromDate, toDate]
  );

  const filteredList = useMemo(() => {
    return patientsList.filter((row) => {
    const p = getPatientFromDetailsMap(patientDetailsMap, row.patientId) || {};
    const assignedDoctor = getAssignedDoctorName(row, doctorsById);
    
    // Global search across all fields
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const values = [
        row.patientId,
        row.patientName,
        p.email,
        p.mobile,
        p.gender,
        p.dob ? (() => {
          const age = Math.max(0, Math.floor((Date.now() - new Date(p.dob).getTime()) / (365.25*24*60*60*1000)));
          return age.toString();
        })() : null,
        p.address,
        p.phone,
        p.aadhar,
        assignedDoctor,
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      
      // Also search in formatted registration and visit dates
      const formattedRegDate = getRegistrationDateKey(p);
      if (formattedRegDate) {
        values.push(formattedRegDate.toLowerCase());
      }
      const formattedVisitDate = toPeriodDateKey(getAppointmentDate(row));
      if (formattedVisitDate) {
        values.push(formattedVisitDate.toLowerCase());
      }
      
      if (!values.some((v) => v.includes(q))) return false;
    }
    
    // Filter by any field (not just name)
    if (filterName.trim()) {
      const n = filterName.toLowerCase();
      const searchableFields = [
        row.patientId,
        row.patientName,
        p.email,
        p.mobile,
        p.gender,
        p.dob ? (() => {
          const age = Math.max(0, Math.floor((Date.now() - new Date(p.dob).getTime()) / (365.25*24*60*60*1000)));
          return age.toString();
        })() : null,
        p.address,
        p.phone,
        p.aadhar,
        assignedDoctor,
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      
      // Also search in formatted registration and visit dates
      const formattedRegDate2 = getRegistrationDateKey(p);
      if (formattedRegDate2) {
        searchableFields.push(formattedRegDate2.toLowerCase());
      }
      const formattedVisitDate2 = toPeriodDateKey(getAppointmentDate(row));
      if (formattedVisitDate2) {
        searchableFields.push(formattedVisitDate2.toLowerCase());
      }
      
      if (!searchableFields.some((v) => v.includes(n))) return false;
    }
    
    // Filter by Gender (exact match)
    if (filterGender.trim()) {
      if (String(p.gender || "").toLowerCase() !== filterGender.toLowerCase()) return false;
    }
    
    // Filter by Age Range
    if (filterAgeMin.trim() || filterAgeMax.trim()) {
      const dob = p.dob;
      if (!dob) return false;
      const age = Math.max(0, Math.floor((Date.now() - new Date(dob).getTime()) / (365.25*24*60*60*1000)));
      
      if (filterAgeMin.trim() && age < parseInt(filterAgeMin)) return false;
      if (filterAgeMax.trim() && age > parseInt(filterAgeMax)) return false;
    }
    
    // Filter by Date Range (visit / appointment date, or registration date for patient-only OPD rows)
    if (visitInPeriodPatientIds) {
      const inVisitPeriod = visitInPeriodPatientIds.has(String(row.patientId));
      if (!inVisitPeriod) {
        if (row.isPatientOnlyRow) {
          const registrationRaw = getPatientRegistrationRaw(p);
          if (
            !isVisitDateInPeriod(
              registrationRaw,
              visitPeriodBounds.start,
              visitPeriodBounds.end,
              fromDate || null,
              toDate || null
            )
          ) {
            return false;
          }
        } else {
          return false;
        }
      }
    }
    
    return true;
  });
  }, [
    patientsList,
    patientDetailsMap,
    doctorsById,
    searchQuery,
    filterName,
    filterGender,
    filterAgeMin,
    filterAgeMax,
    visitInPeriodPatientIds,
    visitPeriodBounds.start,
    visitPeriodBounds.end,
    fromDate,
    toDate,
  ]);

  const periodLabel = formatPeriodLabel(fromDate, toDate);
  const periodSummary = useMemo(
    () =>
      buildOpdPeriodSummary({
        rows: filteredList,
        patientDetailsMap,
        groupedAppointments: appointmentsCache || {},
        doctorsById,
        departmentsById,
        periodStart: visitPeriodBounds.start,
        periodEnd: visitPeriodBounds.end,
        periodStartKey: fromDate || null,
        periodEndKey: toDate || null,
        registrationLookup,
      }),
    [
      filteredList,
      patientDetailsMap,
      appointmentsCache,
      doctorsById,
      departmentsById,
      visitPeriodBounds.start,
      visitPeriodBounds.end,
      fromDate,
      toDate,
      registrationLookup,
    ]
  );

  const clearFilters = () => {
    setSearchQuery("");
    setFilterName("");
    setFilterGender("");
    setFilterAgeMin("");
    setFilterAgeMax("");
    setFromDate("");
    setToDate("");
  };

  const handleExport = async () => {
    const isDaily = isSingleDayPeriod(fromDate || null, toDate || null);
    let invoicesForSummary = periodInvoices;

    if (fromDate || toDate) {
      try {
        // Refresh period invoices so registration-based new/old stays accurate.
        invoicesForSummary = await fetchPeriodInvoicesForSummary(fromDate, toDate);
      } catch (error) {
        console.warn("[PatientsTable] Could not load invoices for export summary:", error);
      }
    }

    // Daily: production path (filtered list + unique-patient summary).
    // Monthly: aligned patient-day rows so Total Patients === Excel row count.
    if (isDaily) {
      const rows = filteredList || [];
      const headers = [
        "Patient ID",
        "Patient Name",
        "Email",
        "Mobile",
        "Gender",
        "Age",
        "Creation Date",
        "Assigned Doctor",
      ];

      const tableRows = rows.map((r) => {
        const p = getPatientFromDetailsMap(patientDetailsMap, r.patientId) || {};
        const age = p.dob
          ? Math.max(
              0,
              Math.floor(
                (Date.now() - new Date(p.dob).getTime()) /
                  (365.25 * 24 * 60 * 60 * 1000)
              )
            )
          : "";

        const formattedCreationDate = getRegistrationDateKey(p) || "";
        const visitDate =
          fromDate ||
          toPeriodDateKey(getAppointmentDate(r)) ||
          "";
        const assignedDoctor =
          resolveAssignedDoctorNameFromAppointments({
            patientId: r.patientId ?? r.patient_id,
            visitDateKey: visitDate || null,
            groupedAppointments: appointmentsCache || {},
            doctorsById,
            fallbackRow: r,
          }) ||
          getAssignedDoctorName(r, doctorsById) ||
          "";

        return [
          r.patientId,
          r.patientName,
          p.email || "",
          p.mobile || "",
          p.gender || "",
          age || "",
          formattedCreationDate || "",
          assignedDoctor || "",
        ];
      });

      await downloadPatientTableExcel({
        filename: `out_patients_${fromDate || "all"}_${toDate || "all"}`,
        sheetName: "Out Patients",
        organizationName: getOrganizationName(user),
        summaryTitle: "Outpatient Summary",
        periodLabel,
        fromDate: fromDate || null,
        toDate: toDate || null,
        summary: buildOpdPeriodSummaryForPeriod({
          groupedAppointments: appointmentsCache || {},
          patientDetailsMap,
          doctorsById,
          departmentsById,
          invoices: invoicesForSummary,
          periodStart: visitPeriodBounds.start,
          periodEnd: visitPeriodBounds.end,
          periodStartKey: fromDate || null,
          periodEndKey: toDate || null,
        }),
        tableHeaders: headers,
        tableRows,
      });
      return;
    }

    // Summary stays invoice-aligned; table rows come from invoice patient-days + doctors.
    const { summary } = buildAlignedOpdPeriodExport({
      groupedAppointments: appointmentsCache || {},
      invoices: invoicesForSummary,
      patientDetailsMap,
      doctorsById,
      departmentsById,
      periodStart: visitPeriodBounds.start,
      periodEnd: visitPeriodBounds.end,
      periodStartKey: fromDate || null,
      periodEndKey: toDate || null,
    });

    // Fresh patient master for Excel details (email/mobile/age/creation date).
    let exportPatientMap = patientDetailsMap;
    try {
      const allPatients = await getAllPatients();
      exportPatientMap = buildPatientDetailsMap(allPatients);
      setPatientDetailsMap(exportPatientMap);
    } catch (error) {
      console.warn("[PatientsTable] Could not refresh patients for export:", error);
    }

    const exportPatientRows = buildMonthlyOpdExcelTableRows({
      invoices: invoicesForSummary,
      groupedAppointments: appointmentsCache || {},
      patientDetailsMap: exportPatientMap,
      doctorsById,
      periodStart: visitPeriodBounds.start,
      periodEnd: visitPeriodBounds.end,
      periodStartKey: fromDate || null,
      periodEndKey: toDate || null,
    });

    // Fetch any patient ids present on invoices but missing from getAllPatients payload
    exportPatientMap = await hydratePatientDetailsForExport(
      exportPatientRows,
      exportPatientMap
    );
    const nameIndex = buildPatientNameIndex(exportPatientMap);

    // Second pass: resolve missing ids by unique name, then hydrate those ids too
    const resolvedRows = exportPatientRows.map((r) => {
      let patientId = r.patientId ?? r.patient_id ?? null;
      const invoiceName = String(r.patientName || r.patient_name || "")
        .trim()
        .toLowerCase();
      if ((patientId == null || patientId === "") && invoiceName) {
        const matches = nameIndex.get(invoiceName) || [];
        if (matches.length === 1) {
          const match = matches[0];
          patientId = match?.id ?? match?.patientId ?? match?.patient_id ?? null;
        }
      }
      return { ...r, patientId };
    });

    exportPatientMap = await hydratePatientDetailsForExport(
      resolvedRows,
      exportPatientMap
    );

    const tableRows = resolvedRows
      .map((r) => {
        let patientId = r.patientId ?? r.patient_id ?? null;
        let p = getPatientFromDetailsMap(exportPatientMap, patientId) || null;

        const invoiceName = String(r.patientName || r.patient_name || "")
          .trim()
          .toLowerCase();
        if (!p && invoiceName) {
          const matches = nameIndex.get(invoiceName) || [];
          if (matches.length === 1) {
            p = matches[0];
            patientId =
              p?.id ?? p?.patientId ?? p?.patient_id ?? patientId ?? null;
          }
        }

        // Skip unresolved identity rows (name+date only) — they create sparse Excel sheets
        if (patientId == null || patientId === "") {
          return null;
        }

        // If master still missing after hydrate, keep the row but avoid blank-looking exports
        // by ensuring we at least output the id + invoice name + visit date.
        const patientName =
          String(r.patientName || r.patient_name || p?.name || "").trim() ||
          `Patient ${patientId}`;

        const fields = getPatientExportFields(p);
        const visitDate =
          r.visitDateKey ||
          toPeriodDateKey(getAppointmentDate(r)) ||
          "";

        let assignedDoctor =
          getAssignedDoctorName(
            {
              ...r,
              doctorId: r.doctorId ?? r.doctor_id,
              doctorName: r.doctorName || r.doctor_name,
              patientId,
            },
            doctorsById
          ) ||
          r.doctorName ||
          r.doctor_name ||
          "";

        if (!assignedDoctor && visitDate) {
          assignedDoctor =
            resolveAssignedDoctorNameFromAppointments({
              patientId,
              visitDateKey: visitDate,
              groupedAppointments: appointmentsCache || {},
              doctorsById,
              fallbackRow: r,
            }) || "";
        }

        return [
          patientId,
          patientName,
          visitDate,
          fields.email,
          fields.mobile,
          fields.gender,
          fields.age,
          fields.creationDate,
          assignedDoctor || "",
        ];
      })
      .filter(Boolean);

    const headers = [
      "Patient ID",
      "Patient Name",
      "Visit Date",
      "Email",
      "Mobile",
      "Gender",
      "Age",
      "Creation Date",
      "Assigned Doctor",
    ];

    await downloadPatientTableExcel({
      filename: `out_patients_${fromDate || "all"}_${toDate || "all"}`,
      sheetName: "Out Patients",
      organizationName: getOrganizationName(user),
      summaryTitle: "Outpatient Summary",
      periodLabel,
      fromDate: fromDate || null,
      toDate: toDate || null,
      summary,
      tableHeaders: headers,
      tableRows,
    });
  };

  const handleMenuClick = (event, patient) => {
    setAnchorEl(event.currentTarget);
    setSelectedPatient(patient);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedPatient(null);
  };

  const handleEdit = async () => {
    if (!selectedPatient?.patientId) return;
    
    // Store patient reference before closing menu
    const patientToEdit = selectedPatient;
    handleCloseMenu();

    try {
      const data = await getPatientById(patientToEdit.patientId);
      setPatientInfo(data);
      setOpenEditModal(true);
    } catch (err) {
      console.error("Failed to fetch patient info:", err);
    }
  };

  const handleAddPrescription = () => {
    if (!selectedPatient) return;
    
    // Verify this patient is assigned to the current doctor (for safety)
    if (currentDoctorId && (user?.roleName === "doctor" || user?.roleName === "surgeon")) {
      if (selectedPatient.doctorId && Number(selectedPatient.doctorId) !== Number(currentDoctorId)) {
        alert("You can only add prescriptions for your assigned patients.");
        handleCloseMenu();
        return;
      }
    }
    
    // Store patient reference before closing menu
    const patientForPrescription = selectedPatient;
    handleCloseMenu();
    
    // Use stored reference to set selected patient
    setSelectedPatient(patientForPrescription);
    // Same Calendar → Tabs → NewPrescription flow as Patient In Queue
    setOpenCalendarModal(true);
  };

  const handleAddVitalSigns = () => {
    if (!selectedPatient?.patientId) return;
    const patientForVitals = selectedPatient;
    handleCloseMenu();
    setVitalsPatient(patientForVitals);
    setVitalsMode("form");
    setVitalsSaving(false);
    setVitalsCanSave(false);
    setOpenVitalsModal(true);
  };

  const handleCloseVitalsModal = () => {
    vitalsRef.current?.resetForm?.();
    setOpenVitalsModal(false);
    setVitalsPatient(null);
    setVitalsMode("form");
    setVitalsSaving(false);
    setVitalsCanSave(false);
  };

  const vitalsAppointmentId = vitalsPatient?.isPatientOnlyRow
    ? null
    : vitalsPatient?.id ?? vitalsPatient?.appointmentId ?? null;

  const vitalsPatientLabel =
    vitalsPatient?.patientName ||
    getPatientFromDetailsMap(patientDetailsMap, vitalsPatient?.patientId)?.name ||
    `Patient #${vitalsPatient?.patientId || ""}`;

  return (
    <Grid container>
      <Grid item xs={12}>
      <Paper sx={{ width: "100%" }}>
        <Box sx={{ p: 1.5, pt: 1, pb: 1 }}>
          <Box sx={TABLE_SEARCH_ROW_SX}>
            <TextField
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={TABLE_ICON_SX} fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={() => setShowFilters((v) => !v)}
              >
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export to Excel">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh (force reload from server)">
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={() => refreshPatientsList()}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {showFilters && (
          <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label="Filter by any field"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
            />
            <TextField
              size="small"
              label="Gender"
              value={filterGender}
              onChange={(e) => setFilterGender(e.target.value)}
              select
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Male">Male</MenuItem>
              <MenuItem value="Female">Female</MenuItem>
            </TextField>
            <TextField
              size="small"
              label="Age Min"
              type="number"
              value={filterAgeMin}
              onChange={(e) => setFilterAgeMin(e.target.value)}
              inputProps={{ min: 0 }}
              sx={{ minWidth: 120 }}
            />
            <TextField
              size="small"
              label="Age Max"
              type="number"
              value={filterAgeMax}
              onChange={(e) => setFilterAgeMax(e.target.value)}
              inputProps={{ min: 0 }}
              sx={{ minWidth: 120 }}
            />
            <TextField
              size="small"
              label="From visit date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="To visit date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Tooltip title="Clear filters">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={clearFilters}>
                <ClearAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          )}
        </Box>
        <TableContainer sx={{ maxHeight: "calc(100vh - 360px)" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <StyledTableCell
                    key={column.id}
                    style={{ top: 57, minWidth: column.minWidth }}
                  >
                    {column.label}
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {fetching ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    <Box sx={{ py: 3 }}>
                      <CircularProgress size={40} />
                      {loadingProgress.message && (
                        <Typography variant="body2" sx={{ mt: 2 }}>
                          {loadingProgress.message}
                        </Typography>
                      )}
                      {loadingProgress.total > 0 && !loadingProgress.message && (
                        <Typography variant="body2" sx={{ mt: 2 }}>
                          Loading details... {loadingProgress.loaded} / {loadingProgress.total}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : filteredList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1}>
                    <Alert severity="info">
                      {patientsList.length === 0 
                        ? "No Patients Found" 
                        : "No records match your search/filter criteria"}
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : (
                filteredList
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow hover key={row.id}>
                      <TableCell>{row.patientId || "-"}</TableCell>
                      <TableCell>
                        <span
                          style={{ color: "#568AF7", cursor: "pointer" }}
                          onClick={() => {
                            const id =
                              row.patientId ??
                              row.patient_id ??
                              getPatientFromDetailsMap(patientDetailsMap, row.patientId)?.id;
                            if (id == null || id === "") return;
                            // We already have this patient's details in memory (from the
                            // getAllPatients() call that populated this table) — hand them
                            // along so the destination page doesn't have to re-fetch
                            // GET /patients/{id} for data we already fetched a click ago.
                            const knownPatient =
                              getPatientFromDetailsMap(patientDetailsMap, row.patientId) || null;
                            if (onPatientClick) {
                              onPatientClick(id, knownPatient);
                            } else {
                              navigate(`/opd-dashboard/${id}`, {
                                state: { tab: "1", patient: knownPatient },
                              });
                            }
                          }}
                        >
                          {row.patientName ||
                            getPatientFromDetailsMap(patientDetailsMap, row.patientId)?.name ||
                            "-"}
                        </span>
                      </TableCell>
                      <TableCell>{patientDetailsMap[row.patientId]?.email || "-"}</TableCell>
                      <TableCell>{patientDetailsMap[row.patientId]?.mobile || "-"}</TableCell>
                      <TableCell>{patientDetailsMap[row.patientId]?.gender || "-"}</TableCell>
                      <TableCell>{(() => {
                        const dob = patientDetailsMap[row.patientId]?.dob;
                        if (!dob) return "-";
                        const age = Math.max(0, Math.floor((Date.now() - new Date(dob).getTime()) / (365.25*24*60*60*1000)));
                        return age;
                      })()}</TableCell>
                      <TableCell>{(() => {
                        const p = getPatientFromDetailsMap(patientDetailsMap, row.patientId) || {};
                        return getRegistrationDateKey(p) || "-";
                      })()}</TableCell>
                      <TableCell>
                        <IconButton onClick={(e) => handleMenuClick(e, row)}>
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={filteredList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setPage(0);
          }}
        />
      </Paper>
      </Grid>

      {/* Dropdown menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
        {/* Edit Patient / New Consultation: frontdesk, admin, super_admin only */}
        {user &&
          ["frontdesk", "admin", "super_admin", "superadmin"].includes(
            String(user.roleName || "").toLowerCase()
          ) && (
          <>
            <MenuItem onClick={handleEdit}>Edit Patient</MenuItem>
            <MenuItem
              onClick={async () => {
                if (!selectedPatient?.patientId) return;
                
                // Store patient reference before closing menu
                const patientForConsultation = selectedPatient;
                handleCloseMenu();

                try {
                  const data = await getPatientById(patientForConsultation.patientId);
                  setNewConsultationPatientInfo(data);
                  setOpenNewConsultationModal(true);
                } catch (err) {
                  console.error("Failed to fetch patient info:", err);
                  // Still open modal with available data
                  setNewConsultationPatientInfo(patientForConsultation);
                  setOpenNewConsultationModal(true);
                }
              }}
            >
              New Consultation
            </MenuItem>
            <MenuItem onClick={handleAddVitalSigns}>Add Vital Signs</MenuItem>
          </>
        )}
        
        {/* Only show Add Prescription for doctors/surgeons and their assigned patients */}
        {user && (user.roleName === "doctor" || user.roleName === "surgeon") && (
          <>
            {currentDoctorId && 
             selectedPatient?.doctorId && 
             Number(selectedPatient.doctorId) === Number(currentDoctorId) ? (
              <MenuItem onClick={handleAddPrescription}>Add Prescription</MenuItem>
            ) : (
              <MenuItem disabled>
                <Typography variant="caption" color="text.secondary">
                  Add Prescription (Not assigned to you)
                </Typography>
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      {/* Edit Patient Modal */}
      <EditPatientModal
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          setSelectedPatient(null);
          // Refresh patient list after modal closes
          refreshPatientsList();
        }}
        patient_info={patientInfo}
      />

      {/* Same Add Prescription shell as Patient In Queue (Calendar → Tabs → NewPrescription) */}
      <Calendar
        open={openCalendarModal}
        onClose={async () => {
          setOpenCalendarModal(false);
          setSelectedPatient(null);
          await refreshPatientsList();
        }}
        appointment={(() => {
          if (!selectedPatient) return {};
          const details =
            getPatientFromDetailsMap(patientDetailsMap, selectedPatient.patientId) ||
            {};
          const appointmentId = selectedPatient.isPatientOnlyRow
            ? null
            : selectedPatient.id ?? selectedPatient.appointmentId ?? null;
          return {
            id: appointmentId,
            appointmentId,
            patientId: selectedPatient.patientId,
            patientName:
              selectedPatient.patientName ||
              details.name ||
              details.patientName ||
              "",
            doctorId:
              currentDoctorId ||
              doctorId ||
              selectedPatient.doctorId ||
              user?.id,
            doctorName:
              selectedPatient.doctorName ||
              user?.username ||
              "",
            gender: details.gender || selectedPatient.gender || "",
            age: details.age || selectedPatient.age || "",
            dob: details.dob || selectedPatient.dob || "",
            mobile: details.mobile || details.phone || selectedPatient.mobile || "",
            address: details.address || selectedPatient.address || "",
            appointmentDateTime: selectedPatient.appointmentDateTime || null,
          };
        })()}
      />

      {/* New Consultation Modal */}
      <NewConsultationModal
        open={openNewConsultationModal}
        onClose={async () => {
          setOpenNewConsultationModal(false);
          setNewConsultationPatientInfo(null);
          await refreshPatientsList();
        }}
        patient_info={newConsultationPatientInfo}
      />

      {/* Add Vital Signs — same front-desk vitals panel as registration/consultation success */}
      <Dialog
        open={openVitalsModal}
        onClose={vitalsSaving ? undefined : handleCloseVitalsModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Vital Signs</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {vitalsPatientLabel}
            {vitalsPatient?.patientId != null
              ? ` (ID: ${vitalsPatient.patientId})`
              : ""}
          </Typography>
          {openVitalsModal && vitalsPatient?.patientId ? (
            <FrontDeskVitalSignsPanel
              ref={vitalsRef}
              patientId={vitalsPatient.patientId}
              appointmentId={vitalsAppointmentId}
              enabled={!!vitalsPatient.patientId}
              compact
              hidePromptButtons
              hideFormActions
              mode={vitalsMode}
              onModeChange={setVitalsMode}
              onSaved={handleCloseVitalsModal}
              onSaveStateChange={({ saving, canSave }) => {
                setVitalsSaving(saving);
                setVitalsCanSave(canSave);
              }}
            />
          ) : (
            <Alert severity="warning">Patient is required to add vital signs.</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            sx={FORM_CANCEL_BUTTON_SX}
            disabled={vitalsSaving}
            onClick={handleCloseVitalsModal}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            sx={FORM_SUBMIT_BUTTON_SX}
            disabled={vitalsSaving || !vitalsCanSave || !vitalsPatient?.patientId}
            onClick={() => vitalsRef.current?.save?.()}
          >
            {vitalsSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default PatientsTable;